import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../config/api';

export interface ConnectionSummary {
  id: string;
  platform: string;
  accountHandle: string;
  /** ISO 3166-1 alpha-2 (uppercase) or null = global brand account. */
  country: string | null;
  scopes: string[];
  metadata: Record<string, unknown>;
  expiresAt: string | null;
  createdAt: string;
}

/**
 * Fetch stored social connections, optionally scoped to a single country.
 *
 *   useConnections()           → full list across all countries
 *   useConnections('global')   → only the brand-level (country IS NULL) rows
 *   useConnections('SE')       → only Sweden-scoped rows
 *
 * The query key includes `country` so each tab caches independently.
 */
export function useConnections(country?: string) {
  const qs = country ? `?country=${encodeURIComponent(country)}` : '';
  return useQuery({
    queryKey: ['connections', country ?? 'all'],
    queryFn: () => api<ConnectionSummary[]>(`/connections${qs}`),
    staleTime: 30 * 1000,
  });
}

export function useDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ deleted: string }>(`/connections/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connections'] });
    },
  });
}
